import { ProgressKind, RequestKind, boolean, string } from '@sb/library/base';
import { BatchResultItem, CalendarBookingInputItem, CalendarDefaultPricesUpdateItem, ColumnMatchInputItem, CreateAttendanceResultItem, DisplayItemCollection, IImportErrorResponse, MoveStudentToFamilyUpdateItem, StudentAttachmentDisplayItem, StudentAttachmentInputItem, StudentAttachmentSearchOptions, StudentAttachmentUpdateItem, StudentColumnMatchesDisplayItem, StudentContactDisplayItem, StudentContactSearchOptions, StudentDisplayItem, TagDisplayItem, TagInputItem, TagSearchOptions, TagUpdateItem, StudentInputItem, StudentNameDisplayItem, StudentNameSearchOptions, StudentProfileDisplayItem, StudentProfileSearchOptions, StudentProfileUpdateItem, StudentSearchOptions, StudentUpdateItem, StudentsImportInputItem } from '@sb/library/data-models';
import { ApiRoute } from '../api-route';

export namespace StudentRoutes {
    export const Add = ApiRoute.Define({
        URL: `students`,
        Kind: RequestKind.Post,
        BodyType: StudentInputItem,
        ResultType: string
    });

    export const GetStudent = ApiRoute.Define({
        URL: (studentID: string) => `students/${studentID}`,
        Kind: RequestKind.Get,
        ResultType: StudentDisplayItem
    });

    export const GetAll = ApiRoute.Define({
        URL: `students`,
        Kind: RequestKind.Get,
        PagingAllowed: true,
        ResultType: DisplayItemCollection<StudentDisplayItem>
    });

    export namespace Names {
        export const Get = ApiRoute.Define({
            URL: `names/students`,
            Kind: RequestKind.Get,
            ResultType: Array<StudentNameDisplayItem>
        });

        export const Search = ApiRoute.Define({
            URL: `search/names/students`,
            Kind: RequestKind.Post,
            BodyType: StudentNameSearchOptions,
            ResultType: Array<StudentNameDisplayItem>
        });
    }

    export const ApplyDefaultPrices = ApiRoute.Define({
        URL: `students/applydefaultprices`,
        Kind: RequestKind.Patch,
        BodyType: CalendarDefaultPricesUpdateItem,
        ResultType: boolean
    });

    export const CanBeDeleted = ApiRoute.Define({
        URL: (studentID: string) => `students/${studentID}/candelete`,
        Kind: RequestKind.Get,
        ResultType: boolean
    });

    export const DeleteStudent = ApiRoute.Define({
        URL: (studentID: string) => `students/${studentID}`,
        Kind: RequestKind.Delete,
        ResultType: boolean
    });

    export const Search = ApiRoute.Define({
        URL: `search/students`,
        Kind: RequestKind.Post,
        BodyType: StudentSearchOptions,
        PagingAllowed: true,
        ResultType: DisplayItemCollection<StudentDisplayItem>
    });

    export const UpdateStudent = ApiRoute.Define({
        URL: (studentID: string) => `students/${studentID}`,
        Kind: RequestKind.Put,
        BodyType: StudentUpdateItem,
        ResultType: boolean
    });

    export namespace Profile {
        export const Update = ApiRoute.Define({
            URL: (studentID: string) => `studentprofiles/${studentID}`,
            Kind: RequestKind.Put,
            BodyType: StudentProfileUpdateItem,
            ResultType: boolean
        });

        export const Get = ApiRoute.Define({
            URL: (studentID: string) => `studentprofiles/${studentID}`,
            Kind: RequestKind.Get,
            ResultType: StudentProfileDisplayItem
        });

        export const GetAll = ApiRoute.Define({
            URL: `studentprofiles`,
            Kind: RequestKind.Get,
            ResultType: DisplayItemCollection<StudentProfileDisplayItem>
        });

        export const Search = ApiRoute.Define({
            URL: `search/studentprofiles`,
            Kind: RequestKind.Post,
            BodyType: StudentProfileSearchOptions,
            ResultType: DisplayItemCollection<StudentProfileDisplayItem>
        });

        export namespace Image {
            export const Delete = ApiRoute.Define({
                URL: (studentID: string) => `students/${studentID}/image`,
                Kind: RequestKind.Delete,
                ResultType: boolean
            });

            export const Update = ApiRoute.Define({
                URL: (studentID: string) => `students/${studentID}/image`,
                Kind: RequestKind.Put,
                BodyType: FormData,
                ResultType: string
            });

            export const Preview = ApiRoute.Define({
                URL: `students/imagepreview`,
                Kind: RequestKind.Post,
                BodyType: FormData,
                ResultType: string
            });
        }
    }

    export namespace Contact {
        export const Get = ApiRoute.Define({
            URL: (studentID: string) => `studentcontacts/${studentID}`,
            Kind: RequestKind.Get,
            ResultType: StudentContactDisplayItem
        });

        export const GetAll = ApiRoute.Define({
            URL: `studentcontacts`,
            Kind: RequestKind.Get,
            PagingAllowed: true,
            ResultType: DisplayItemCollection<StudentContactDisplayItem>
        });

        export const Search = ApiRoute.Define({
            URL: `search/studentcontacts`,
            Kind: RequestKind.Post,
            BodyType: StudentContactSearchOptions,
            PagingAllowed: true,
            ResultType: DisplayItemCollection<StudentContactDisplayItem>
        });
    }

    export namespace Attachment {
        export const Add = ApiRoute.Define({
            URL: `studentattachments`,
            Kind: RequestKind.Post,
            BodyType: StudentAttachmentInputItem,
            ResultType: string
        });

        export const Get = ApiRoute.Define({
            URL: (studentAttachmentID: string) => `studentattachments/${studentAttachmentID}`,
            Kind: RequestKind.Get,
            ResultType: StudentAttachmentDisplayItem
        });

        export const Search = ApiRoute.Define({
            URL: `search/studentattachments`,
            Kind: RequestKind.Post,
            BodyType: StudentAttachmentSearchOptions,
            ResultType: DisplayItemCollection<StudentAttachmentDisplayItem>
        });

        export const Delete = ApiRoute.Define({
            URL: (studentAttachmentID: string) => `studentattachments/${studentAttachmentID}`,
            Kind: RequestKind.Delete,
            ResultType: boolean
        });

        export const Update = ApiRoute.Define({
            URL: (studentAttachmentID: string) => `studentattachments/${studentAttachmentID}`,
            Kind: RequestKind.Put,
            BodyType: StudentAttachmentUpdateItem,
            ResultType: boolean
        });
    }

    export const MoveToFamily = ApiRoute.Define({
        URL: (studentID: string) => `students/${studentID}/movetofamily`,
        Kind: RequestKind.Put,
        BodyType: MoveStudentToFamilyUpdateItem,
        ResultType: boolean
    });

    export const Register = ApiRoute.Define({
        URL: (studentID: string) => `students/${studentID}/attendance`,
        Kind: RequestKind.Post,
        BodyType: CalendarBookingInputItem,
        ResultType: CreateAttendanceResultItem
    });

    export namespace Import {
        export const MatchColumns = ApiRoute.Define({
            URL: `students/import/matchcolumns`,
            Kind: RequestKind.Post,
            BodyType: Array<ColumnMatchInputItem>,
            ResultType: StudentColumnMatchesDisplayItem
        });

        export const Import = ApiRoute.Define({
            URL: `students/import`,
            Kind: RequestKind.Post,
            BodyType: StudentsImportInputItem,
            ResultType: Array<BatchResultItem<boolean, IImportErrorResponse>>,
            ExtraParameters: 'dryRun',
            ShowProgress: ProgressKind.Computation
        });
    }
}
